import React, { useState } from "react";
import { getSortedFields, getFieldDisplayName, searchFields } from "../utils/fieldMapping";

const GroupingBuilder = ({ fieldMetadata, grouping, onChange }) => {
  const [newGrouping, setNewGrouping] = useState({
    field: '',
    type: 'field',
    startIndex: 0,
    length: 4,
    ranges: [
      { min: '', max: '', label: '' }
    ]
  });

  // Типы группировки
  const groupingTypes = [
    { value: 'field', label: 'По полю целиком' },
    { value: 'substring', label: 'По подстроке (например, первые 4 символа)' },
    { value: 'range', label: 'По диапазонам значений' }
  ];

  // Расширенный список fallback полей для группировки
  const fallbackFields = [
    // Основные поля для группировки
    { value: 'stageId', label: 'Стадия', type: 'select' },
    { value: 'assignedById', label: 'Ответственный', type: 'number' },
    { value: 'createdBy', label: 'Кто создал', type: 'number' },
    { value: 'companyId', label: 'Компания', type: 'number' },
    { value: 'contactId', label: 'Контакт', type: 'number' },

    // Строковые поля для группировки
    { value: 'title', label: 'Название', type: 'string' },
    { value: 'ufCrm3_1746789101', label: 'Номер заказа', type: 'string' },
    { value: 'ufCrm3_1746789474', label: 'ID-Франчайзи', type: 'string' },
    { value: 'ufCrm3_1746789363', label: 'Размер стекла', type: 'string' },
    { value: 'ufCrm3_1746789446', label: 'Толщина стекла', type: 'string' },
    { value: 'ufCrm3_1746789468', label: 'Тип стекла', type: 'string' },
    { value: 'ufCrm3_1746789572', label: 'Обработка', type: 'string' },
    { value: 'ufCrm3_1746789650', label: 'Статус оплаты', type: 'string' },

    // Числовые поля для группировки по диапазонам
    { value: 'id', label: 'ID', type: 'number' },
    { value: 'opportunity', label: 'Сумма', type: 'number' },
    { value: 'ufCrm3_1746789589', label: 'Количество', type: 'number' },
    { value: 'ufCrm3_1746789599', label: 'Цена за единицу', type: 'number' },
    { value: 'ufCrm3_1746789627', label: 'Общая стоимость', type: 'number' },

    // Временные поля для группировки
    { value: 'createdTime', label: 'Дата создания', type: 'date' },
    { value: 'updatedTime', label: 'Дата изменения', type: 'date' },
    { value: 'ufCrm3_1746789890', label: 'Дата доставки', type: 'date' }
  ];

  // Получение доступных полей
  const getAvailableFields = () => {
    console.log('GroupingBuilder - fieldMetadata:', fieldMetadata);
    console.log('GroupingBuilder - fieldMetadata keys count:', Object.keys(fieldMetadata || {}).length);

    // Получаем все поля из маппинга
    const mappedFields = getSortedFields().map(field => ({
      value: field.id,
      label: field.name,
      type: getFieldTypeFromMapping(field.id)
    }));

    console.log('GroupingBuilder - поля из маппинга:', mappedFields.length);
    return mappedFields;
  };

  // Определяем тип поля на основе его ID
  const getFieldTypeFromMapping = (fieldId) => {
    const dateFields = [
      'createdTime', 'updatedTime',
      'ufCrm3_1749213244986', 'ufCrm3_1749213350972', 'ufCrm3_1749555592378'
    ];

    const numberFields = [
      'id', 'assignedById', 'opportunity', 'companyId', 'contactId', 'createdBy'
    ];

    if (dateFields.includes(fieldId)) return 'date';
    if (numberFields.includes(fieldId)) return 'number';
    return 'string';
  };

  // Добавление новой группировки
  const addGrouping = () => {
    if (!newGrouping.field) {
      alert('Пожалуйста, выберите поле для группировки');
      return;
    }

    // Валидация для диапазонов
    if (newGrouping.type === 'range') {
      const validRanges = newGrouping.ranges.filter(range =>
        range.min !== '' && range.max !== '' && range.label !== ''
      );

      if (validRanges.length === 0) {
        alert('Пожалуйста, добавьте хотя бы один валидный диапазон');
        return;
      }

      newGrouping.ranges = validRanges;
    }

    const groupingToAdd = {
      id: Date.now(),
      ...newGrouping
    };

    onChange([...grouping, groupingToAdd]);

    // Сброс формы
    setNewGrouping({
      field: '',
      type: 'field',
      startIndex: 0,
      length: 4,
      ranges: [{ min: '', max: '', label: '' }]
    });
  };

  // Удаление группировки
  const removeGrouping = (groupingId) => {
    onChange(grouping.filter(g => g.id !== groupingId));
  };

  // Обновление группировки
  const updateGrouping = (groupingId, updates) => {
    onChange(grouping.map(g => g.id === groupingId ? { ...g, ...updates } : g));
  };

  // Получение описания группировки
  const getGroupingDescription = (group) => {
    const field = getAvailableFields().find(f => f.value === group.field);
    if (!field) return '';

    let description = `Группировка по "${field.label}"`;

    switch (group.type) {
      case 'substring':
        description += ` (символы с ${group.startIndex} по ${group.startIndex + group.length})`;
        break;
      case 'range':
        const rangeCount = group.ranges ? group.ranges.length : 0;
        description += ` (${rangeCount} диапазонов)`;
        break;
      default:
        description += ' (полное значение)';
    }

    return description;
  };

  // Добавление нового диапазона для группировки по диапазонам
  const addRange = () => {
    setNewGrouping({
      ...newGrouping,
      ranges: [...newGrouping.ranges, { min: '', max: '', label: '' }]
    });
  };

  // Удаление диапазона
  const removeRange = (index) => {
    const newRanges = newGrouping.ranges.filter((_, i) => i !== index);
    setNewGrouping({
      ...newGrouping,
      ranges: newRanges.length > 0 ? newRanges : [{ min: '', max: '', label: '' }]
    });
  };

  // Обновление диапазона
  const updateRange = (index, field, value) => {
    const newRanges = newGrouping.ranges.map((range, i) =>
      i === index ? { ...range, [field]: value } : range
    );
    setNewGrouping({
      ...newGrouping,
      ranges: newRanges
    });
  };

  // Перемещение группировки вверх/вниз
  const moveGrouping = (groupingId, direction) => {
    const currentIndex = grouping.findIndex(g => g.id === groupingId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= grouping.length) return;

    const newGrouping = [...grouping];
    [newGrouping[currentIndex], newGrouping[newIndex]] = [newGrouping[newIndex], newGrouping[currentIndex]];
    onChange(newGrouping);
  };

  const availableFields = getAvailableFields();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Настройка группировки</h3>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <div>
            <h4 className="text-green-900 font-medium">Группировка данных</h4>
            <p className="text-green-700 text-sm mt-1">
              Объедините данные по полям или их частям. Например, группировка франчайзи по первым 4 символам (АЛМА, АСТН, АТЫР) для анализа по регионам.
            </p>
          </div>
        </div>
      </div>

      {/* Список существующих группировок */}
      {grouping.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Активные группировки ({grouping.length})</h4>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              ⚠️ Порядок группировок важен! Данные будут сначала группироваться по первой группировке, затем по второй и т.д.
            </p>
          </div>

          {grouping.map((group, index) => (
            <div key={group.id} className="bg-gray-50 border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                  <div className="text-sm font-medium text-gray-900">
                    {getGroupingDescription(group)}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {index > 0 && (
                    <button
                      onClick={() => moveGrouping(group.id, 'up')}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title="Переместить вверх"
                    >
                      ↑
                    </button>
                  )}
                  {index < grouping.length - 1 && (
                    <button
                      onClick={() => moveGrouping(group.id, 'down')}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title="Переместить вниз"
                    >
                      ↓
                    </button>
                  )}
                  <button
                    onClick={() => removeGrouping(group.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Удалить
                  </button>
                </div>
              </div>

              {/* Детали группировки */}
              {group.type === 'substring' && (
                <div className="text-xs text-gray-600 mt-2">
                  Берутся символы с позиции {group.startIndex}, длина {group.length}
                </div>
              )}

              {group.type === 'range' && group.ranges && (
                <div className="mt-2">
                  <div className="text-xs text-gray-600 mb-2">Диапазоны:</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {group.ranges.map((range, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border">
                        {range.label}: {range.min} - {range.max}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Форма добавления новой группировки */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4">Добавить группировку</h4>

        <div className="space-y-4">
          {/* Выбор поля */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Поле для группировки
              </label>
              <select
                value={newGrouping.field}
                onChange={(e) => setNewGrouping({...newGrouping, field: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите поле</option>
                {availableFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Тип группировки
              </label>
              <select
                value={newGrouping.type}
                onChange={(e) => setNewGrouping({...newGrouping, type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!newGrouping.field}
              >
                {groupingTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Настройки для подстроки */}
          {newGrouping.type === 'substring' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-blue-900 font-medium mb-3">Настройки подстроки</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-2">
                    Начальная позиция (0 = первый символ)
                  </label>
                  <input
                    type="number"
                    value={newGrouping.startIndex}
                    onChange={(e) => setNewGrouping({...newGrouping, startIndex: parseInt(e.target.value) || 0})}
                    min="0"
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-2">
                    Количество символов
                  </label>
                  <input
                    type="number"
                    value={newGrouping.length}
                    onChange={(e) => setNewGrouping({...newGrouping, length: parseInt(e.target.value) || 1})}
                    min="1"
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-3 text-sm text-blue-700">
                <strong>Пример:</strong> Для ID франчайзи "АЛМА-001" с позицией 0 и длиной 4 получится группа "АЛМА"
              </div>
            </div>
          )}

          {/* Настройки для диапазонов */}
          {newGrouping.type === 'range' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-purple-900 font-medium">Настройки диапазонов</h5>
                <button
                  onClick={addRange}
                  className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                >
                  + Добавить диапазон
                </button>
              </div>

              <div className="space-y-3">
                {newGrouping.ranges.map((range, index) => (
                  <div key={index} className="bg-white border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-900">
                        Диапазон #{index + 1}
                      </span>
                      {newGrouping.ranges.length > 1 && (
                        <button
                          onClick={() => removeRange(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Удалить
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Минимум</label>
                        <input
                          type="text"
                          value={range.min}
                          onChange={(e) => updateRange(index, 'min', e.target.value)}
                          placeholder="0"
                          className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Максимум</label>
                        <input
                          type="text"
                          value={range.max}
                          onChange={(e) => updateRange(index, 'max', e.target.value)}
                          placeholder="1000"
                          className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Название группы</label>
                        <input
                          type="text"
                          value={range.label}
                          onChange={(e) => updateRange(index, 'label', e.target.value)}
                          placeholder="Малые заказы"
                          className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 text-sm text-purple-700">
                <strong>Пример:</strong> Группировка заказов по сумме: 0-10000 = "Малые", 10001-50000 = "Средние", 50001+ = "Крупные"
              </div>
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={addGrouping}
              disabled={!newGrouping.field}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              + Добавить группировку
            </button>
          </div>
        </div>
      </div>

      {/* Примеры использования */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-yellow-900 font-medium mb-2">💡 Примеры группировок</h4>
        <div className="text-yellow-800 text-sm space-y-1">
          <div><strong>По регионам франчайзи:</strong> ID-Франчайзи → Подстрока (позиция 0, длина 4) → Группы: АЛМА, АСТН, АТЫР</div>
          <div><strong>По размеру заказа:</strong> Сумма → Диапазоны → 0-10000 (Малые), 10001-50000 (Средние), 50001+ (Крупные)</div>
          <div><strong>По месяцам:</strong> Дата создания → Подстрока (позиция 0, длина 7) → Группы: 2024-01, 2024-02, 2024-03</div>
          <div><strong>По стадиям:</strong> Стадия → По полю → Группы по значениям стадий</div>
        </div>
      </div>
    </div>
  );
};

export default GroupingBuilder;